import { Injectable, inject } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
// From @primeuix/styled, NOT @primeuix/themes: themes 1.x bundles its own newer
// copy of styled, so its updatePreset changes a theme instance PrimeNG 19 never
// reads. This must be the same copy primeng depends on.
import { palette, updatePreset } from '@primeuix/styled';
import { Theme } from '../models/theme.model';
import { FirebaseThemeService } from './firebase-theme.service';

const HEX_COLOR = /^\s*#(?:[0-9a-f]{3}|[0-9a-f]{6})\s*$/i;
/** Mirrors assets/styles/customTheme/definePreset.ts, to restore it. */
const DEFAULT_PRIMARY_PALETTE = Object.fromEntries(
  ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'].map((s) => [s, `{indigo.${s}}`])
);
/** Inline color overrides set in applyTheme; cleared again by resetColors. */
const THEME_COLOR_PROPS = [
  '--p-primary-color',
  '--p-button-primary-color',
  '--p-button-primary-hover-background',
  '--p-button-primary-hover-border-color',
  '--p-button-primary-hover-color',
  '--p-button-outlined-primary-border-color',
  '--p-button-outlined-primary-color',
  '--p-button-outlined-primary-hover-border-color',
  '--p-button-outlined-primary-hover-color',
];

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private theme: Theme | null = null;
  private isInitialized: boolean = false;
  private activeRouteName: string | null = null;
  private themeSubscription?: Subscription;
  /** Last colors handed to PrimeNG; starts as the preset default. */
  private appliedColorsKey = 'preset';
  private firebaseThemeService = inject(FirebaseThemeService);
  private readonly DEFAULT_BUTTON_PRIMARY = '#3B82F6';
  private readonly DEFAULT_BUTTON_TEXT = '#FFFFFF';

  /** Public observable for components to reactively read theme state */
  readonly theme$: Observable<Theme | null> = this.firebaseThemeService.getTheme$();

  initializeTheme(routeName: string): void {
    if (this.activeRouteName === routeName && this.isInitialized) {
      return;
    }
    this.activeRouteName = routeName;
    this.themeSubscription?.unsubscribe();

    this.firebaseThemeService.initializeThemeListener(routeName);

    this.themeSubscription = this.theme$.subscribe({
      next: (themeFromFirebase) => {
        if (themeFromFirebase) {
          this.theme = themeFromFirebase;

          // Always apply images and fonts
          this.applyImages();
          this.applyTheme();
          this.isInitialized = true;
        } else {
          // A unit without a theme must not inherit the previous unit's colors.
          this.resetColors();
          this.isInitialized = false;
        }
      },
      error: () => {
        this.isInitialized = false;
      }
    });
  }

  private loadGoogleFont(fontName: string): void {
    if (!fontName || typeof document === 'undefined') return;
    const cleanFont = fontName.trim();
    if (!cleanFont || cleanFont.toLowerCase() === 'system-ui' || cleanFont.toLowerCase() === 'sans-serif') return;

    const fontId = `google-font-${cleanFont.replace(/\s+/g, '-').toLowerCase()}`;
    if (document.getElementById(fontId)) return;

    const link = document.createElement('link');
    link.id = fontId;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(cleanFont)}:wght@300;400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }

  private applyImages(): void {
    if (!this.theme) {
      return;
    }

    const root = document.documentElement;

    // Apply images
    if (this.theme.images?.logo) {
      root.style.setProperty('--p-image-logo', `url('${this.theme.images.logo}')`);
    }
    if (this.theme.images?.heroImage) {
      root.style.setProperty('--p-image-hero', `url('${this.theme.images.heroImage}')`);
    }
  }

  private applyTheme(): void {
    if (!this.theme) {
      return;
    }

    const root = document.documentElement;
    const primary = HEX_COLOR.test(this.theme.colors?.primary ?? '')
      ? this.theme.colors!.primary!.trim()
      : this.DEFAULT_BUTTON_PRIMARY;
    const buttonText = HEX_COLOR.test(this.theme.colors?.buttonText ?? '')
      ? this.theme.colors!.buttonText!.trim()
      : this.DEFAULT_BUTTON_TEXT;

    this.applyPrimaryPalette(primary, buttonText);
    root.style.setProperty('--p-primary-color', primary);
    root.style.setProperty('--p-button-primary-color', buttonText);
    root.style.setProperty('--p-button-primary-hover-background', primary);
    root.style.setProperty('--p-button-primary-hover-border-color', primary);
    root.style.setProperty('--p-button-primary-hover-color', buttonText);
    root.style.setProperty('--p-button-outlined-primary-border-color', primary);
    root.style.setProperty('--p-button-outlined-primary-color', primary);
    root.style.setProperty('--p-button-outlined-primary-hover-border-color', primary);
    root.style.setProperty('--p-button-outlined-primary-hover-color', primary);

    if (this.theme.fonts) {
      const fonts = this.theme.fonts;

      this.loadGoogleFont(fonts.heading);
      this.loadGoogleFont(fonts.body);

      root.style.setProperty('--p-font-family', fonts.primary || fonts.body);
      root.style.setProperty('--p-font-family-heading', fonts.heading, 'important');
      root.style.setProperty('--p-font-family-body', fonts.body, 'important');
      root.style.setProperty('--p-font-family-secondary', fonts.secondary || fonts.body, 'important');
      root.style.setProperty('--p-font-family-hero-title', fonts.heroImageTitle || fonts.heading, 'important');
      root.style.setProperty('--p-font-family-hero-body', fonts.heroImageBody || fonts.body, 'important');
      root.style.setProperty('--p-font-family-card-title', fonts.cardTitle || fonts.heading, 'important');
      root.style.setProperty('--p-font-family-card-body', fonts.cardBody || fonts.body, 'important');
    }
  }

  /**
   * Generates the whole PrimeNG primary palette (50-950, brand color at 500)
   * from `primary`, so the tints and shades used across the app (e.g. the order
   * confirmation page's --p-primary-50 badges) follow the unit's color too, not
   * just --p-primary-color. `buttonText` becomes the contrast color on primary.
   */
  private applyPrimaryPalette(primary: string, buttonText: string): void {
    const key = `${primary}|${buttonText}`;
    if (key === this.appliedColorsKey) {
      return; // updatePreset regenerates the theme CSS; skip when nothing changed
    }
    this.appliedColorsKey = key;
    updatePreset({
      semantic: {
        primary: palette(primary),
        colorScheme: { light: { primary: { contrastColor: buttonText } } },
      },
    });
  }

  /** Back to the preset look for a unit with no theme at all. */
  private resetColors(): void {
    for (const prop of THEME_COLOR_PROPS) {
      document.documentElement.style.removeProperty(prop);
    }
    if (this.appliedColorsKey === 'preset') {
      return;
    }
    this.appliedColorsKey = 'preset';
    updatePreset({
      semantic: {
        primary: DEFAULT_PRIMARY_PALETTE,
        colorScheme: { light: { primary: { contrastColor: '#ffffff' } } },
      },
    });
  }

  getTheme(): Theme | null {
    return this.theme;
  }

  isThemeInitialized(): boolean {
    return this.isInitialized;
  }

  updateTheme(newTheme: Partial<Theme>): void {
    if (this.theme) {
      this.theme = { ...this.theme, ...newTheme } as Theme;
      this.applyTheme();
    }
  }
}
